import React, {memo} from "react";
import "../index.scss";
import { Typography } from "components/atoms";
import SparkleIcon from "assets/mcat-chat/mcat-chat-icon.svg";
import SparkleIconDark from "assets/mcat-chat/mcat-chat-icon-dark.svg";
import exclamationIcon from "assets/mcat-chat/exclamation.svg";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../molecules/ToolTip";
import exclamationDarkIcon from "assets/mcat-chat/exclamation-dark.svg";

interface ChatDrawerHeaderProps {
  title: string;
  subtitle?: string;
  selectedSubject?: string;
  isMobile?: boolean;
  theme: "light" | "dark";
  // Exam info for practice review mode
  examName?: string;
  section?: string;
  questionNumber?: string;
  isPracticeReviewMode?: boolean;
}

export const ChatDrawerHeader: React.FC<ChatDrawerHeaderProps> = memo((
    {
       title,
       subtitle,
       selectedSubject,
       isMobile = false,
       theme,
       examName,
       section,
       questionNumber,
       isPracticeReviewMode = false,
     }) => {
  return (
      <div className="chat-drawer-header">
        <div className="chat-header-content">
          <div className="chat-header">
            {/* Only show title in non-mobile view since it's already in the header actions */}
            {!isMobile && (
                <div className="chat-header-content-new">
                  <div className="chat-title-row">
                    {/* Sparkle icon inline with title */}
                    <div className="title-with-icon">
                      <img
                          src={theme === "dark" ? SparkleIconDark : SparkleIcon}
                          alt="AI Tutor Sparkle"
                          className="sparkle-icon-inline"
                      />
                      <Typography variant="h6Bold" label={title} />
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        <img
                            src={
                              theme === "dark" ? exclamationDarkIcon : exclamationIcon
                            }
                            className="exclamation-icon"
                        />
                      </TooltipTrigger>
                    <TooltipContent className={`mcat-info-tooltip ${theme}`}>
                      The AI Tutor helps you strengthen your understanding of core exam subjects through interactive, question-driven learning. While it covers a broad range of topics, some responses may go beyond what is required for the exam. Be sure to use your course materials for exam-specific guidance.
                      <br />
                      <br />
                      At this time, the AI Tutor does not support advanced math, accept file uploads, or generate images. As with any AI tool, it is important to verify responses. For best results, use the tutor as a helpful companion alongside your course materials.
                    </TooltipContent>
                    </Tooltip>
                  </div>
                  <div className="chat-tagline">
                    <Typography 
                      variant="h9Book" 
                      label="Powered by The Princeton Review's proven methods and materials." 
                      styles="tagline-text"
                    />
                  </div>
                </div>
            )}

            {(selectedSubject || isPracticeReviewMode) && (
                <div className="chat-header-sub">
                  <div className="current-focus-banner">
                    <Typography variant="h9Medium" label="Current Chat Focus" />
                  </div>
                  {isPracticeReviewMode ? (
                    // Practice review mode - show exam info
                    <>
                      {(examName || section) && (
                        <div className="chat-header-subject">
                          <Typography 
                            variant="h8Bold" 
                            label={examName && section ? `${examName}: ${section}` : (examName || section || '')} 
                          />
                        </div>
                      )}
                      {questionNumber && (
                        <div className="chat-header-subtitle">
                          <Typography variant="h9Book" label={questionNumber} />
                        </div>
                      )}
                    </>
                  ) : (
                    // Manual mode - show subject
                    <>
                      <div className="chat-header-subject">
                        {selectedSubject && (
                            <Typography variant="h8Bold" label={selectedSubject} />
                        )}
                      </div>
                      {subtitle && (
                          <div className="chat-header-subtitle">
                            <Typography variant="h9Book" label={subtitle} />
                          </div>
                      )}
                    </>
                  )}
                </div>
            )}
          </div>
        </div>
      </div>
  );
});
